import React, { useState, useEffect } from 'react';
// Comment out unused imports for now in case they're needed later
// import { getGoals, getTakeaways, getLastChatSummary } from '../lib/api/coachApi';

// Radar chart component for psychometric data
const RadarChart = ({ data }) => {
  const { metrics } = data;
  const maxValue = 10; // Maximum value for the radar chart
  const centerX = 120; // Center X coordinate (half of the viewBox width)
  const centerY = 120; // Center Y coordinate (half of the viewBox height)
  const radius = 70; // Radius of the chart

  // Generate points for the radar chart
  const generatePoints = () => {
    const totalMetrics = metrics.length;
    let points = '';
    
    metrics.forEach((metric, index) => {
      // Calculate the angle for this point
      const angle = (Math.PI * 2 * index) / totalMetrics - Math.PI / 2;
      // Calculate the distance from center based on value
      const distance = (metric.value / maxValue) * radius;
      // Calculate x and y coordinates
      const x = centerX + distance * Math.cos(angle);
      const y = centerY + distance * Math.sin(angle);
      
      // Append to points string
      points += (index === 0 ? 'M' : 'L') + x + ',' + y;
    });
    
    // Close the path
    points += 'Z';
    return points;
  };

  // Generate circles for the background
  const generateCircles = () => {
    const circles = [];
    const steps = 5; // Number of concentric circles
    
    for (let i = 1; i <= steps; i++) {
      const circleRadius = (radius * i) / steps;
      circles.push(
        <circle 
          key={`circle-${i}`}
          cx={centerX}
          cy={centerY}
          r={circleRadius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      );
    }
    
    return circles;
  };

  // Generate axis lines
  const generateAxisLines = () => {
    const totalMetrics = metrics.length;
    const lines = [];
    
    metrics.forEach((_, index) => {
      const angle = (Math.PI * 2 * index) / totalMetrics - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      
      lines.push(
        <line
          key={`line-${index}`}
          x1={centerX}
          y1={centerY}
          x2={x}
          y2={y}
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      );
    });
    
    return lines;
  };

  // Generate labels for each axis
  const generateLabels = () => {
    const totalMetrics = metrics.length;
    const labels = [];
    
    metrics.forEach((metric, index) => {
      const angle = (Math.PI * 2 * index) / totalMetrics - Math.PI / 2;
      // Position labels slightly outside the chart
      const labelRadius = radius + 22;
      const x = centerX + labelRadius * Math.cos(angle);
      const y = centerY + labelRadius * Math.sin(angle);
      
      // Adjust text anchor based on position
      let textAnchor = "middle";
      if (x < centerX - 10) textAnchor = "end";
      if (x > centerX + 10) textAnchor = "start";
      
      labels.push(
        <text
          key={`label-${index}`}
          x={x}
          y={y}
          textAnchor={textAnchor}
          dominantBaseline="middle"
          fill="#4b5563"
          fontSize="10"
          fontWeight="500"
        >
          {metric.name}
        </text>
      );
    });
    
    return labels;
  };

  return (
    <div className="insights-page__radar-chart w-full max-w-sm mx-auto mt-4">
      <svg viewBox="0 0 240 240" className="w-full">
        {/* Background circles */}
        {generateCircles()}
        
        {/* Axis lines */}
        {generateAxisLines()}
        
        {/* Data polygon */}
        <path
          d={generatePoints()}
          fill="rgba(59, 130, 246, 0.2)"
          stroke="#3b82f6"
          strokeWidth="2"
        />
        
        {/* Data points */}
        {metrics.map((metric, index) => {
          const angle = (Math.PI * 2 * index) / metrics.length - Math.PI / 2;
          const distance = (metric.value / maxValue) * radius;
          const x = centerX + distance * Math.cos(angle);
          const y = centerY + distance * Math.sin(angle);
          
          return (
            <circle
              key={`point-${index}`}
              cx={x}
              cy={y}
              r="3"
              fill="#3b82f6"
              stroke="#fff"
              strokeWidth="1"
            />
          );
        })}
        
        {/* Labels */}
        {generateLabels()}
      </svg>
      
      {/* Legend */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        {metrics.map((metric, index) => (
          <div key={`legend-${index}`} className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-gray-700">{metric.name}: {metric.value}/10</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Loading indicator component
const LoadingIndicator = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
  </div>
);

// Error message component
const ErrorMessage = ({ message }) => (
  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
    <p>{message || 'An error occurred while fetching data'}</p>
  </div>
);

const InsightsPage = () => {
  // State for API data
  const [goals, setGoals] = useState([]);
  const [takeaways, setTakeaways] = useState([]);
  const [chatSummary, setChatSummary] = useState(null);
  // Remove unused chatSummaries state
  
  // Loading and error states
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [loadingTakeaways, setLoadingTakeaways] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  // Remove unused loadingChatSummaries state
  
  const [errorGoals, setErrorGoals] = useState(null);
  const [errorTakeaways, setErrorTakeaways] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);
  // Remove unused errorChatSummaries state
  
  // Dummy data for Counter-Strike player psychometric
  const lastPsychometric = {
    title: "Competitive Performance Assessment",
    date: "2024-04-10",
    metrics: [
      { name: "Mental Toughness", value: 8.5 },
      { name: "Tilt Resistance", value: 6.2 },
      { name: "Focus", value: 7.8 },
      { name: "Team Communication", value: 9.0 },
      { name: "Adaptability", value: 7.5 },
      { name: "Pressure Handling", value: 6.8 }
    ]
  };

  // Dummy Counterstrike player goals
  const csPlayerGoals = [
    {
      name: "Improve Tilt Management",
      description: "Develop techniques to remain calm during high-pressure situations and after setbacks.",
      progress: "In Progress (Improving)"
    },
    {
      name: "Enhance Team Communication",
      description: "Refine callouts and strategic communications during competitive matches.",
      progress: "In Progress (Backsliding)"
    },
    {
      name: "Pre-match Mental Preparation",
      description: "Establish a consistent routine before matches to optimize focus and performance.",
      progress: "Recently Started (No Progress Yet)"
    }
  ];

  // Dummy coach's notes for CS player
  const csCoachNotes = [
    {
      content: "Shows excellent strategic awareness but needs to work on emotional regulation during critical rounds.",
      createdDate: "2024-04-08"
    },
    {
      content: "Communication patterns improve significantly when playing with regular teammates. Consider applying similar approaches with new teammates.",
      createdDate: "2024-04-02"
    },
    {
      content: "Demonstrated impressive resilience during tournament quarterfinals. Build on this mental strength for future competitions.",
      createdDate: "2024-03-25"
    }
  ];

  // Dummy chat summary for CS player
  const csChatSummary = {
    date: "2024-04-10",
    summary: "Discussed strategies for maintaining focus during long tournament days. Identified specific triggers that affect performance during multi-map series.",
    keyInfo: "Player shows strong tactical understanding but can benefit from mindfulness techniques between maps to reset mental state and prevent performance decline in extended series."
  };

  // Fetch data from API on component mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch goals (using dummy data for this demo)
      try {
        setLoadingGoals(true);
        // For demo, we'll use our dummy data instead of the API call
        // const response = await getGoals();
        // setGoals(response.goals || []);
        setGoals(csPlayerGoals);
        setErrorGoals(null);
      } catch (error) {
        console.error('Error fetching goals:', error);
        setErrorGoals(error.message);
      } finally {
        setLoadingGoals(false);
      }
      
      // Fetch takeaways (using dummy data for this demo)
      try {
        setLoadingTakeaways(true);
        // For demo, we'll use our dummy data instead of the API call
        // const response = await getTakeaways();
        // setTakeaways(response.takeaways || []);
        setTakeaways(csCoachNotes);
        setErrorTakeaways(null);
      } catch (error) {
        console.error('Error fetching takeaways:', error);
        setErrorTakeaways(error.message);
      } finally {
        setLoadingTakeaways(false);
      }
      
      // Fetch chat summary (using dummy data for this demo)
      try {
        setLoadingSummary(true);
        // For demo, we'll use our dummy data instead of the API call
        // const response = await getLastChatSummary();
        // setChatSummary(response);
        setChatSummary(csChatSummary);
        setErrorSummary(null);
      } catch (error) {
        console.error('Error fetching chat summary:', error);
        setErrorSummary(error.message);
      } finally {
        setLoadingSummary(false);
      }
    };
    
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <div className="insights-page container mx-auto px-4 py-8 max-w-4xl">
      <div className="insights-page__header mb-6">
        <h1 className="insights-page__title text-4xl font-bold text-gray-800 mb-3">
          Your Insights
        </h1>
        <p className="insights-page__subtitle text-lg text-gray-600 mb-2">
          Track your progress, review feedback, and discover personalized insights.
        </p>
        <p className="insights-page__demo-notice text-sm text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-md inline-block">
          For this demo, this content is not based on your actual conversations with Pokket.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Top Left - Goals Section */}
        <section className="insights-page__goals bg-white rounded-xl card-shadow p-6">
          <h2 className="insights-page__section-title text-2xl font-semibold mb-4">Goals</h2>
          
          {loadingGoals && <LoadingIndicator />}
          
          {errorGoals && <ErrorMessage message={errorGoals} />}
          
          {!loadingGoals && !errorGoals && goals.length === 0 && (
            <p className="text-gray-500 italic">No goals found. Start chatting with Pokket to set some goals!</p>
          )}
          
          {!loadingGoals && !errorGoals && goals.length > 0 && (
            <ul className="space-y-4">
              {goals.map((goal, index) => (
                <li key={index} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                  <h3 className="font-medium text-gray-800">{goal.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{goal.description}</p>
                  <div className="mt-2 text-xs font-medium text-gray-500 bg-gray-100 inline-block px-2 py-1 rounded">
                    {goal.progress}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Top Right - Recent Psychometric Section */}
        <section className="insights-page__recent-psychometric bg-white rounded-xl card-shadow p-6">
          <h2 className="insights-page__section-title text-2xl font-semibold mb-4">Recent Psychometric</h2>
          <p className="text-lg font-medium mb-2">{lastPsychometric.title}</p>
          <p className="text-sm text-gray-500 mb-4">Completed: {lastPsychometric.date}</p>
          
          <RadarChart data={lastPsychometric} />
          
          <div className="mt-4 text-center">
            <button className="text-blue-600 text-sm font-medium hover:text-blue-800 transition-colors">
              View Full Assessment Results
            </button>
          </div>
        </section>

        {/* Bottom Left - Coach's Notes Section */}
        <section className="insights-page__coach-notes bg-white rounded-xl card-shadow p-6">
          <h2 className="insights-page__section-title text-2xl font-semibold mb-4">Coach's Notes</h2>
          
          {loadingTakeaways && <LoadingIndicator />}
          
          {errorTakeaways && <ErrorMessage message={errorTakeaways} />}
          
          {!loadingTakeaways && !errorTakeaways && (!takeaways || takeaways.length === 0) && (
            <p className="text-gray-500 italic">No coaching notes yet. Continue your conversations with Pokket to receive personalized advice.</p>
          )}
          
          {!loadingTakeaways && !errorTakeaways && takeaways && takeaways.length > 0 && (
            <div className="space-y-4">
              {takeaways.map((note, index) => (
                <div key={index} className="border-l-2 border-blue-500 pl-4 py-1">
                  <p className="text-gray-700">{note.content}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatDate(note.createdDate)}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bottom Right - Pokket's Insights Section */}
        <section className="insights-page__pokket-insights bg-white rounded-xl card-shadow p-6">
          <h2 className="insights-page__section-title text-2xl font-semibold mb-4">Pokket's Insights</h2>
          
          {loadingSummary && <LoadingIndicator />}
          
          {errorSummary && <ErrorMessage message={errorSummary} />}
          
          {!loadingSummary && !errorSummary && !chatSummary && (
            <p className="text-gray-500 italic">No chat summary available. Have more conversations with Pokket to see insights here.</p>
          )}
          
          {!loadingSummary && !errorSummary && chatSummary && (
            <div>
              {chatSummary.date && (
                <p className="text-xs text-gray-500 mb-2">
                  From conversation on {formatDate(chatSummary.date)}
                </p>
              )}
              
              {chatSummary.summary && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-1">Summary</h3>
                  <p className="text-gray-700">{chatSummary.summary}</p>
                </div>
              )}
              
              {chatSummary.keyInfo && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-1">Key Insights</h3>
                  <p className="text-gray-700">{chatSummary.keyInfo}</p>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default InsightsPage; 